// The dice bag's expression field (issue 1541, slice 2): "1d20 + 2", "2d6+3", "d%", "d8 - 1", "3 coins" typed in and
// thrown as one roll. It reads only what the person typed into a request the bag already sends (`roll.request`: one die
// kind, a count and a modifier); the server still throws every face. Anything a request cannot carry — two kinds of die,
// a multiplier — is refused here in words, so nothing is sent that the table would refuse.
import { DICE_LIMITS, type Die } from '@dragons-table/vtt-shared';

export type Expression = { die: Die; quantity: number; modifier: number };

const TERM = /^([+-])?\s*(?:(\d*)\s*d\s*(\d+|%)|(\d+))$/i;

/** The roll an expression stands for, or the reason it is not one. */
export function parseExpression(text: string): Expression | { error: string } {
  const t = text.trim().toLowerCase();
  if (!t) return { error: 'Type dice to roll, such as 1d20 + 2.' };
  const coins = /^(\d*)\s*coins?$/.exec(t);
  if (coins) {
    const quantity = coins[1] ? Number(coins[1]) : 1;
    if (quantity < 1 || quantity > DICE_LIMITS.quantity) return { error: `From 1 to ${DICE_LIMITS.quantity} coins at once.` };
    return { die: { kind: 'coin' }, quantity, modifier: 0 };
  }
  // Split into signed terms: "1d20 + 2 - 1" reads as 1d20, +2, -1.
  const terms = t.replace(/\s+/g, '').match(/[+-]?[^+-]+/g) ?? [];
  let die: Die | null = null;
  let quantity = 0;
  let modifier = 0;
  for (const raw of terms) {
    const m = TERM.exec(raw);
    if (!m) return { error: `"${raw.replace(/^[+-]/, '')}" is not dice or a number.` };
    const sign = m[1] === '-' ? -1 : 1;
    if (m[4] !== undefined) { modifier += sign * Number(m[4]); continue; }
    if (sign < 0) return { error: 'Dice can be added, not taken away.' };
    const sides = m[3] === '%' ? 100 : Number(m[3]);
    const count = m[2] ? Number(m[2]) : 1;
    if (die && die.kind === 'd' && die.sides !== sides) return { error: 'One kind of die a roll: roll the others on their own.' };
    if (sides < 2 || sides > DICE_LIMITS.sides) return { error: `A die has 2 to ${DICE_LIMITS.sides} sides.` };
    die = { kind: 'd', sides };
    quantity += count;
  }
  if (!die) return { error: 'Name a die to roll, such as d20.' };
  if (quantity < 1 || quantity > DICE_LIMITS.quantity) return { error: `From 1 to ${DICE_LIMITS.quantity} dice at once.` };
  if (Math.abs(modifier) > DICE_LIMITS.modifier) return { error: `A modifier of up to ${DICE_LIMITS.modifier} either way.` };
  return { die, quantity, modifier };
}
